<template>
  <section class="card coop-page">
    <h2>跨组织合作</h2>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <div v-if="profile" class="config-panel">
      <h3 class="sub-h">平台配置</h3>
      <div class="config-grid">
        <label class="cfg-field">
          <span>平台名称</span>
          <input v-model="configForm.orgName" class="cfg-input" />
        </label>
        <label class="cfg-field">
          <span>套餐标签</span>
          <input v-model="configForm.planLabel" class="cfg-input" />
        </label>
        <label class="cfg-field">
          <span>合作总额度（家）</span>
          <input v-model.number="configForm.quotaTotal" type="number" min="1" class="cfg-input" />
        </label>
        <label class="cfg-field cfg-field--logo">
          <span>平台 Logo</span>
          <ImageUploadField
            v-model="configForm.logoUrl"
            placeholder="上传 Logo"
            hint="建议正方形 PNG/JPG，不超过 10MB"
            @error="onLogoUploadError"
          />
        </label>
      </div>
      <div class="config-meta">
        <span>剩余额度：<strong>{{ profile.quotaRemaining }}</strong> / {{ profile.quotaTotal }}</span>
        <span class="code-meta">合作码：{{ profile.cooperationCode }}</span>
      </div>
      <div class="config-actions">
        <button type="button" class="btn btn-primary" @click="saveConfig">保存配置</button>
        <button type="button" class="btn btn-cancel" @click="regenerateCode">重置合作码</button>
      </div>
    </div>

    <div class="tab-row">
      <button
        v-for="t in mainTabs"
        :key="t.value"
        type="button"
        class="tab-btn"
        :class="{ 'tab-btn--on': mainTab === t.value }"
        @click="switchMainTab(t.value)"
      >{{ t.label }}</button>
    </div>

    <div v-if="showDirectionTabs" class="capsule-row">
      <button
        type="button"
        class="cap-btn"
        :class="{ 'cap-btn--on': direction === 'INITIATED' }"
        @click="switchDirection('INITIATED')"
      >我发起的</button>
      <button
        type="button"
        class="cap-btn"
        :class="{ 'cap-btn--on': direction === 'RECEIVED' }"
        @click="switchDirection('RECEIVED')"
      >我收到的</button>
    </div>

    <div class="toolbar">
      <button type="button" class="btn btn-primary" @click="openAdd">添加合作单位</button>
    </div>

    <table>
      <thead>
        <tr>
          <th class="col-seq">序号</th>
          <th>对方组织</th>
          <th>联系人</th>
          <th>电话</th>
          <th>状态</th>
          <th>创建时间</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, idx) in pagedRows" :key="row.id">
          <td class="col-seq">{{ rowOffset + idx + 1 }}</td>
          <td>{{ row.partnerOrgName || '—' }}</td>
          <td>{{ row.partnerContactName || '—' }}</td>
          <td>{{ row.partnerContactPhone || '—' }}</td>
          <td><span class="pill">{{ row.statusLabel || '—' }}</span></td>
          <td>{{ row.createdAt || '—' }}</td>
          <td class="col-ops">
            <button type="button" class="btn btn-edit" @click="openDetail(row)">查看</button>
          </td>
        </tr>
      </tbody>
    </table>
    <p v-if="!rows.length && !loading" class="muted empty-hint">暂无内容</p>
    <ListPager
      v-model:page="page"
      v-model:page-size="pageSize"
      :total="rows.length"
    />

    <div v-if="showAdd" class="mask" @click.self="showAdd = false">
      <div class="dialog dialog--shell" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">添加合作单位</h3>
          <button type="button" class="dialog-close" @click="showAdd = false">×</button>
        </div>
        <div class="dialog-body">
          <label class="dlg-field"><span>对方组织名称</span><input v-model="addForm.partnerOrgName" class="dlg-input" /></label>
          <label class="dlg-field"><span>联系人</span><input v-model="addForm.partnerContactName" class="dlg-input" /></label>
          <label class="dlg-field"><span>电话</span><input v-model="addForm.partnerContactPhone" class="dlg-input" /></label>
          <label class="dlg-field"><span>备注</span><textarea v-model="addForm.remark" class="dlg-area" rows="3" /></label>
          <div class="dlg-type">
            <button type="button" class="cap-btn" :class="{ 'cap-btn--on': addTarget === 'plan' }" @click="addTarget = 'plan'">计划合作</button>
            <button type="button" class="cap-btn" :class="{ 'cap-btn--on': addTarget === 'org' }" @click="addTarget = 'org'">组织申请</button>
          </div>
        </div>
        <div class="dialog-foot dialog-foot--split">
          <button type="button" class="btn btn-cancel" @click="showAdd = false">取消</button>
          <button type="button" class="btn btn-primary" @click="submitAdd">保存</button>
        </div>
      </div>
    </div>

    <div v-if="detail" class="mask" @click.self="detail = null">
      <div class="dialog dialog--shell dialog--detail" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">{{ detail.partnerOrgName || '合作详情' }}</h3>
          <button type="button" class="dialog-close" @click="detail = null">×</button>
        </div>
        <div class="dialog-body">
          <div v-if="!detailEditing" class="detail-grid">
            <div class="detail-row"><span class="detail-k">状态</span><span class="detail-v">{{ detail.statusLabel }}</span></div>
            <div class="detail-row"><span class="detail-k">联系人</span><span class="detail-v">{{ detail.partnerContactName || '—' }}</span></div>
            <div class="detail-row"><span class="detail-k">电话</span><span class="detail-v">{{ detail.partnerContactPhone || '—' }}</span></div>
            <div class="detail-row detail-row--block"><span class="detail-k">备注</span><span class="detail-v">{{ detail.remark || '—' }}</span></div>
          </div>
          <div v-else class="detail-edit">
            <label class="dlg-field"><span>对方组织名称</span><input v-model="detailEditForm.partnerOrgName" class="dlg-input" /></label>
            <label class="dlg-field"><span>联系人</span><input v-model="detailEditForm.partnerContactName" class="dlg-input" /></label>
            <label class="dlg-field"><span>电话</span><input v-model="detailEditForm.partnerContactPhone" class="dlg-input" /></label>
            <label class="dlg-field"><span>备注</span><textarea v-model="detailEditForm.remark" class="dlg-area" rows="3" /></label>
          </div>
        </div>
        <div class="dialog-foot dialog-foot--split">
          <button type="button" class="btn btn-cancel" @click="closeDetail">关闭</button>
          <div class="dialog-foot-actions">
            <template v-if="detailEditing">
              <button type="button" class="btn btn-primary" @click="saveDetailEdit">保存</button>
              <button type="button" class="btn btn-cancel" @click="detailEditing = false">取消</button>
            </template>
            <template v-else>
              <button v-if="detail.canEdit" type="button" class="btn btn-edit" @click="startDetailEdit">编辑信息</button>
              <button v-if="detail.canReview" type="button" class="btn btn-primary" @click="review(true)">通过</button>
              <button v-if="detail.canReview" type="button" class="btn btn-delete" @click="review(false)">驳回</button>
            <button v-if="detail.canWithdraw" type="button" class="btn btn-cancel" @click="withdraw">撤回</button>
            <button v-if="detail.canConvertToApplication" type="button" class="btn btn-primary" @click="convertPlan">转正式申请</button>
            <button v-if="detail.canRequestTerminate" type="button" class="btn btn-delete" @click="terminateRequest">申请解除</button>
            <button v-if="detail.canApproveTerminate" type="button" class="btn btn-primary" @click="terminateConfirm">确认解除</button>
            <button v-if="detail.canConvertToApplication" type="button" class="btn btn-delete" @click="deletePlan">删除计划</button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { adminConfirm, adminPrompt } from '@/utils/adminDialog'
import {
  confirmCooperationTerminateAdmin,
  convertCooperationPlanAdmin,
  createCooperationRecordAdmin,
  deleteCooperationPlanAdmin,
  getCooperationDetailAdmin,
  getCooperationProfileAdmin,
  listCooperationRecordsAdmin,
  requestCooperationTerminateAdmin,
  reviewCooperationRecordAdmin,
  saveCooperationProfileAdmin,
  updateCooperationRecordAdmin,
  withdrawCooperationRecordAdmin,
} from '@/api'
import ImageUploadField from '@/components/cms/ImageUploadField.vue'
import ListPager from '@/components/ListPager.vue'
import { clampPage, pageRowOffset, slicePageRows } from '@/utils/listPager'

const MAIN_TABS = [
  { value: 'active', label: '已合作资源' },
  { value: 'resource', label: '资源申请' },
  { value: 'org', label: '组织申请' },
  { value: 'plan', label: '计划合作' },
]

function emptyAddForm() {
  return { partnerOrgName: '', partnerContactName: '', partnerContactPhone: '', remark: '' }
}

function emptyDetailEditForm() {
  return { partnerOrgName: '', partnerContactName: '', partnerContactPhone: '', remark: '', partnerOrgCode: '' }
}

export default {
  name: 'CooperationManageView',
  components: { ImageUploadField, ListPager },
  data() {
    return {
      mainTabs: MAIN_TABS,
      mainTab: 'org',
      direction: 'INITIATED',
      profile: null,
      configForm: { orgName: '', logoUrl: '', planLabel: '', quotaTotal: 100 },
      rows: [],
      listTotal: 0,
      page: 1,
      pageSize: 10,
      loading: false,
      errorMsg: '',
      showAdd: false,
      addTarget: 'plan',
      addForm: emptyAddForm(),
      detail: null,
      detailEditing: false,
      detailEditForm: emptyDetailEditForm(),
    }
  },
  computed: {
    showDirectionTabs() {
      return this.mainTab !== 'plan'
    },
    rowOffset() {
      return pageRowOffset(this.page, this.pageSize)
    },
    pagedRows() {
      return slicePageRows(this.rows, this.page, this.pageSize)
    },
  },
  watch: {
    'rows.length'(n) {
      this.page = clampPage(this.page, n, this.pageSize)
    },
  },
  mounted() {
    this.loadAll()
  },
  methods: {
    async loadAll() {
      this.errorMsg = ''
      this.loading = true
      try {
        this.profile = await getCooperationProfileAdmin()
        this.configForm = {
          orgName: this.profile.orgName || '',
          logoUrl: this.profile.logoUrl || '',
          planLabel: this.profile.planLabel || '',
          quotaTotal: this.profile.quotaTotal || 100,
        }
        await this.loadList()
      } catch (e) {
        this.errorMsg = e.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    async loadList() {
      const res = await listCooperationRecordsAdmin({
        mainTab: this.mainTab,
        direction: this.showDirectionTabs ? this.direction : '',
        page: 1,
        pageSize: 500,
      })
      const items = Array.isArray(res?.items) ? res.items : []
      this.rows = items
      this.listTotal = items.length
      this.page = clampPage(this.page, this.listTotal, this.pageSize)
    },
    switchMainTab(v) {
      this.mainTab = v
      this.page = 1
      this.loadList().catch((e) => { this.errorMsg = e.message || '加载失败' })
    },
    switchDirection(v) {
      this.direction = v
      this.page = 1
      this.loadList().catch((e) => { this.errorMsg = e.message || '加载失败' })
    },
    async saveConfig() {
      try {
        this.profile = await saveCooperationProfileAdmin({ ...this.configForm, regenerateCode: false })
        this.configForm.quotaTotal = this.profile.quotaTotal
        alert('已保存')
      } catch (e) {
        alert(e.message || '保存失败')
      }
    },
    async regenerateCode() {
      if (!await adminConfirm('确认重置合作码？旧码将失效')) return
      try {
        this.profile = await saveCooperationProfileAdmin({ ...this.configForm, regenerateCode: true })
        alert('合作码已重置')
      } catch (e) {
        alert(e.message || '操作失败')
      }
    },
    onLogoUploadError(err) {
      alert((err && err.message) || 'Logo 上传失败')
    },
    openAdd() {
      this.addForm = emptyAddForm()
      this.addTarget = this.mainTab === 'plan' ? 'plan' : 'org'
      this.showAdd = true
    },
    async submitAdd() {
      const name = String(this.addForm.partnerOrgName || '').trim()
      if (!name) {
        alert('请填写对方组织名称')
        return
      }
      try {
        await createCooperationRecordAdmin({
          target: this.addTarget,
          partnerOrgName: name,
          partnerContactName: this.addForm.partnerContactName,
          partnerContactPhone: this.addForm.partnerContactPhone,
          remark: this.addForm.remark,
          direction: this.addTarget === 'org' && this.direction === 'RECEIVED' ? 'RECEIVED' : 'INITIATED',
        })
        this.showAdd = false
        if (this.addTarget === 'plan') this.mainTab = 'plan'
        else this.mainTab = 'org'
        await this.loadAll()
      } catch (e) {
        alert(e.message || '保存失败')
      }
    },
    async openDetail(row) {
      try {
        this.detail = await getCooperationDetailAdmin(row.id)
        this.detailEditing = false
        this.fillDetailEditForm(this.detail)
      } catch (e) {
        alert(e.message || '加载失败')
      }
    },
    closeDetail() {
      this.detail = null
      this.detailEditing = false
    },
    fillDetailEditForm(d) {
      const x = d || {}
      this.detailEditForm = {
        partnerOrgName: x.partnerOrgName || '',
        partnerContactName: x.partnerContactName || '',
        partnerContactPhone: x.partnerContactPhone || '',
        remark: x.remark || '',
        partnerOrgCode: x.partnerOrgCode || '',
      }
    },
    startDetailEdit() {
      this.fillDetailEditForm(this.detail)
      this.detailEditing = true
    },
    async saveDetailEdit() {
      const name = String(this.detailEditForm.partnerOrgName || '').trim()
      if (!name) {
        alert('请填写对方组织名称')
        return
      }
      try {
        this.detail = await updateCooperationRecordAdmin(this.detail.id, {
          partnerOrgName: name,
          partnerOrgCode: this.detailEditForm.partnerOrgCode || '',
          partnerContactName: this.detailEditForm.partnerContactName || '',
          partnerContactPhone: this.detailEditForm.partnerContactPhone || '',
          remark: this.detailEditForm.remark || '',
        })
        this.detailEditing = false
        await this.loadList()
      } catch (e) {
        alert(e.message || '保存失败')
      }
    },
    async review(approved) {
      let note = ''
      if (!approved) {
        note = await adminPrompt('驳回备注（必填）') || ''
        if (!note.trim()) return
      } else if (!await adminConfirm('确认通过？')) return
      try {
        this.detail = await reviewCooperationRecordAdmin(this.detail.id, approved, note)
        await this.loadAll()
      } catch (e) {
        alert(e.message || '操作失败')
      }
    },
    async withdraw() {
      if (!await adminConfirm('确认撤回？')) return
      try {
        this.detail = await withdrawCooperationRecordAdmin(this.detail.id)
        await this.loadList()
      } catch (e) {
        alert(e.message || '操作失败')
      }
    },
    async convertPlan() {
      if (!await adminConfirm('转为正式组织申请？')) return
      try {
        this.detail = await convertCooperationPlanAdmin(this.detail.id)
        await this.loadAll()
      } catch (e) {
        alert(e.message || '操作失败')
      }
    },
    async deletePlan() {
      if (!await adminConfirm('确认删除计划合作？')) return
      try {
        await deleteCooperationPlanAdmin(this.detail.id)
        this.detail = null
        await this.loadList()
      } catch (e) {
        alert(e.message || '操作失败')
      }
    },
    async terminateRequest() {
      if (!await adminConfirm('发起解除？需对方确认')) return
      try {
        this.detail = await requestCooperationTerminateAdmin(this.detail.id)
        await this.loadList()
      } catch (e) {
        alert(e.message || '操作失败')
      }
    },
    async terminateConfirm() {
      if (!await adminConfirm('确认解除合作？额度将退回')) return
      try {
        this.detail = await confirmCooperationTerminateAdmin(this.detail.id)
        await this.loadAll()
      } catch (e) {
        alert(e.message || '操作失败')
      }
    },
  },
}
</script>

<style scoped>
.coop-page .sub-h { margin: 0 0 12px; font-size: 16px; }
.config-panel { margin-bottom: 20px; padding: 16px; background: #f9fafb; border-radius: 8px; }
.config-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.cfg-field { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #475467; }
.cfg-input { height: 36px; padding: 0 10px; border: 1px solid #d0d5dd; border-radius: 6px; }
.cfg-field--logo { grid-column: 1 / -1; }
.config-meta { margin-top: 12px; display: flex; flex-wrap: wrap; gap: 16px; font-size: 13px; color: #475467; }
.code-meta { font-family: monospace; }
.config-actions { margin-top: 12px; display: flex; gap: 8px; }
.tab-row { display: flex; gap: 8px; margin-bottom: 12px; border-bottom: 1px solid #eaecf0; }
.tab-btn { border: none; background: none; padding: 10px 14px; cursor: pointer; color: #667085; border-bottom: 2px solid transparent; margin-bottom: -1px; }
.tab-btn--on { color: #2f6eff; font-weight: 600; border-bottom-color: #2f6eff; }
.capsule-row, .dlg-type { display: flex; gap: 8px; margin-bottom: 12px; }
.cap-btn { flex: 1; border: none; border-radius: 999px; padding: 8px 12px; background: #eef4ff; color: #2f6eff; cursor: pointer; }
.cap-btn--on { background: #2f6eff; color: #fff; font-weight: 600; }
.toolbar { margin-bottom: 12px; }
.empty-hint {
  margin: 16px 0 0;
  text-align: center;
  color: #98a2b3;
  font-size: 13px;
}
.dlg-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; font-size: 13px; }
.dlg-input, .dlg-area { border: 1px solid #d0d5dd; border-radius: 6px; padding: 8px 10px; }
</style>
